// useTableHandlers.js

import { useCallback } from 'react';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';

import { getFieldType, isInlineEditable } from 'views/table/utils/tableFieldUtils';

const useTableHandlers = ({
    openNode,
    addTypeKey,
    onQuickActionCreateOnly,
    onQuickActionSelect,
    handleStartEdit,
    handleModalOpen,
}) => {
    const handleOpenNode = useCallback((node) => {
        if (!node?.key) return;
        if (typeof openNode !== 'function') return;
        openNode(node);
    }, [openNode]);

    const handleAddRow = useCallback(() => {
        if (!addTypeKey) return;
        const handler = onQuickActionCreateOnly || onQuickActionSelect;
        if (!handler) return;
        handler(addTypeKey);
    }, [addTypeKey, onQuickActionCreateOnly, onQuickActionSelect]);

    const handleCellActivate = useCallback((node, fieldDef, fieldKey) => {
        if (!node?.key) return;
        if (fieldKey === 'dateCreated' || fieldKey === 'dateModified') return;
        const fieldType = getFieldType(fieldDef, fieldKey);
        if (fieldType === 'percentage') {
            handleModalOpen('percentage', node, fieldDef, fieldKey);
            return;
        }
        if (isInlineEditable(fieldDef, fieldKey)) {
            handleStartEdit(node, fieldDef, fieldKey);
            return;
        }

        // Single-select enum/pipeline cells use EnumPillSelect inline — no modal.
        if (isEnumCompatibleFieldType(fieldType)) {
            return;
        }
        if (fieldType === 'tags') {
            handleModalOpen('tags', node, fieldDef, fieldKey);
            return;
        }
        if (fieldType === 'date') {
            handleModalOpen('date', node, fieldDef, fieldKey);
            return;
        }
        if (fieldType === 'daterange') {
            handleModalOpen('daterange', node, fieldDef, fieldKey);
            return;
        }
        if (fieldType === 'datetime') {
            handleModalOpen('datetime', node, fieldDef, fieldKey);
            return;
        }
        if (fieldType === 'datetimerange') {
            handleModalOpen('datetimerange', node, fieldDef, fieldKey);
            return;
        }
        if (fieldType === 'location') {
            handleModalOpen('location', node, fieldDef, fieldKey);
            return;
        }
        if (fieldType === 'people') {
            handleModalOpen('people', node, fieldDef, fieldKey);
            return;
        }
        if (fieldType === 'relation') {
            handleModalOpen('relation', node, fieldDef, fieldKey);
            return;
        }
        if (fieldType === 'checklist') {
            handleOpenNode(node);
        }
    }, [handleModalOpen, handleOpenNode, handleStartEdit]);

    return {
        handleAddRow,
        handleCellActivate,
        handleOpenNode,
    };
};

export default useTableHandlers;
